import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { Navigate, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { ADMIN_ACCESS_TOKEN_KEY, getFetcher } from '../../../App';
import Worklist from '@components/organisms/Table/Worklist';
import { Pagination } from '@components/organisms';
import { DEFAULT_COUNT_PER_PAGE, DEFAULT_CURRENT_PAGE } from '@components/organisms/Table/Pagination';
import { dialogContentsState } from '@components/templates';
import { PrimaryButton, Sub1Button } from '@components/atoms';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { headCase } from 'utils/lib';
import {
  StyledFlexColumnsWrapper,
  StyledStatusWrapper,
  StyledBodyRow,
  StyledDelete,
  LicenseHistoryBody,
} from './style';

export interface ParsedWorkListItem {
  id: number;
  activate_date: string;
  count: string | number;
  delete: string | React.JSX.Element;
  expiration_date: string;
  products: string[];
  no: number;
  period: string;
  status: string;
  type: string;
  user: string;
  version: string;
}

export const getStartNumber = (totalCount: number, countPerPage: number, page: number) =>
  totalCount - (page - 1) * countPerPage;

const HistoryPage = () => {
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState('');
  const [conditionsValue, setConditionsValue] = useState({
    countPerPage: DEFAULT_COUNT_PER_PAGE,
    currentPage: DEFAULT_CURRENT_PAGE,
    totalCount: 1,
    'order.name': 'id',
    'order.sort': 'DESC',
  });
  const setDialogContents = useSetRecoilState(dialogContentsState);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  const url = `api/license/history?counrPerPage=${conditionsValue.countPerPage}&currentPage=${conditionsValue.currentPage}`;
  const { data, error } = useSWR(!!accessToken.length ? [{ url, accessToken }] : null);

  if (error) {
    return <div>failed to load</div>;
  }

  if (!data) {
    return <div>loading...</div>;
  }

  const getDisplayLabel = ({
    from,
    to,
    suffix = '',
    isUnlimited,
  }: {
    from: number;
    to?: number;
    suffix?: string;
    isUnlimited: boolean;
  }) => {
    if (isUnlimited) {
      return 'Unlimited';
    } else {
      let _count = 0;
      if (typeof from === 'number' && from !== 0) _count = from;
      return `${_count}${to ? ' / ' + to : ''}${suffix}`;
    }
  };

  let licenseHistories: ParsedWorkListItem[] = [];
  if (data) {
    if (data.code === 'UNAUTHORIZED') return <Navigate to={'/'} replace={true} />;

    if (data.code === '200') {
      const { countPerPage, currentPage, totalCount } = data.result.condition;
      const startNum = getStartNumber(totalCount, countPerPage, currentPage);
      data.result.results.forEach((item: any, index: number) => {
        licenseHistories.push({
          id: item.id,
          no: startNum - index,
          type: item.type,
          products: item.products,
          version: item.version,
          activate_date: item.fromDate,
          expiration_date: item.toDate || '-',
          period: getDisplayLabel({
            from: item.limitPeriod,
            suffix: 'M',
            isUnlimited: item.isUnlimitedPeriod,
          }),
          count: getDisplayLabel({
            from: item.usedCount,
            to: item.limitCount,
            isUnlimited: item.isUnlimitedCount,
          }),
          user: getDisplayLabel({
            from: item.status === 'ACTIVE' ? item.activatedUserCount : item.limitUser,
            to: item.status === 'ACTIVE' ? item.limitUser : '',
            isUnlimited: item.isUnlimitedUser,
          }),
          status: item.status,
          delete: '-',
        });
      });
    }
  }

  const moveToDetailItemPage = (item: ParsedWorkListItem) => {
    navigate(`/license/history/detail/${item.id}`);
  };

  const deleteWaitingLicense = (e: any, licenseId: number) => {
    e.stopPropagation();
    setDialogContents({
      type: 'warning',
      header: '',
      body: 'Are you sure you want to delete LicenseInfo?\n' + 'You can’t cancel if you run it.\n',
      footer: (
        <>
          <Sub1Button onClick={() => setDialogContents({ type: 'hidden' })}>Cancel</Sub1Button>
          <PrimaryButton
            onClick={async () => {
              const url = `api/license/${licenseId}`;
              const response = await getFetcher({
                method: 'DELETE',
                accessToken,
              })(url);
              if (response.status === 'SUCCESS' && response.code === '200') {
                setDialogContents({
                  type: 'success',
                  header: ``,
                  body: <>{'License Delete is complete successfully.'}</>,
                  footer: (
                    <PrimaryButton
                      onClick={() => {
                        setDialogContents({ type: 'hidden' });
                        navigate('/license/history');
                      }}
                    >
                      ok
                    </PrimaryButton>
                  ),
                });
              } else {
                let header = '';
                let body = '';
                if (ErrorCode[response.code]) {
                  header = `${ErrorCode[response.code]}`;
                  body = `${ErrorMessage[ErrorCode[response.code]]}`;
                } else {
                  header = response.code;
                }

                setDialogContents({
                  type: 'error',
                  header: `${header}`,
                  body: <>{body}</>,
                  footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
                });
              }
            }}
          >
            Confirm
          </PrimaryButton>
        </>
      ),
    });
  };

  return (
    <>
      <label>License History</label>
      <LicenseHistoryBody data-page-type={'table-only'}>
        <StyledFlexColumnsWrapper>
          <Worklist
            headers={[
              { label: 'NO', data_label: 'no' },
              { label: 'Type', data_label: 'type' },
              // { label: 'Version', data_label: 'version' }
              // 2023-03-27 version 컬럼 삭제
              // https://www.figma.com/file/dd6hYY7qYoW9R256K4bHRQ/Product-Admin?node-id=415-4940&t=vVv7JePumwszu01T-0
              { label: 'Product', data_label: 'products' },
              { label: 'Activate Date', data_label: 'activate_date' },
              { label: 'Expiration Date', data_label: 'expiration_date' },
              { label: 'Period', data_label: 'period' },
              { label: 'Count', data_label: 'count' },
              { label: 'User', data_label: 'user' },
              { label: 'Status', data_label: 'status' },
              { label: 'Delete', data_label: 'delete' },
            ]}
            highlightColumn={'status'}
            data={{
              dataArray: licenseHistories,
            }}
            customColumn={{
              products: (row) => {
                return (
                  <ul>
                    {row.products.map((product: string, i: number) => {
                      return (
                        <li key={i}>
                          {product}
                          {i !== row.products.length - 1 ? ',' : ''}
                        </li>
                      );
                    })}
                  </ul>
                );
              },
              status: (row) => {
                // row.status
                // ACTIVE | DELETE | WATING_AUTO(시작일 자동 갱신) | WATING_DATE(시작일 지정 갱신) | EXPIRED
                const isWaiting = ['WAITING', 'WAITING_AUTO', 'WAITING_DATE'].includes(row.status);

                return (
                  <StyledStatusWrapper
                    className={isWaiting ? 'waiting' : ''}
                    data-status={row.status}
                    onClick={() => {
                      if (!isWaiting) moveToDetailItemPage(row);
                    }}
                  >
                    {row.status === 'ACTIVE' ? 'In Use' : headCase(row.status)}
                  </StyledStatusWrapper>
                );
              },
              delete: (row) => {
                if (row.status.startsWith('WAITING')) {
                  return (
                    <Sub1Button styles={StyledDelete} onClick={(e) => deleteWaitingLicense(e, row.id)}>
                      Delete
                    </Sub1Button>
                  );
                }
                return <>-</>;
              },
            }}
            styles={{ tbody: StyledBodyRow }}
          />
          <Pagination conditions={data.result?.condition ?? conditionsValue} setConditions={setConditionsValue} />
        </StyledFlexColumnsWrapper>
      </LicenseHistoryBody>
    </>
  );
};

export default HistoryPage;
